import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { engines, transmissions, bodyParts, type SparePart } from "@/mocks/products";

type Category = SparePart["category"];

interface DbProduct {
  id: string;
  category: string;
  name: string;
  make: string | null;
  model: string | null;
  year_range: string | null;
  fitment: string | null;
  price: string | null;
  warranty: string | null;
  condition: string | null;
  stock: string | null;
  description: string | null;
  image_url: string | null;
}

const FALLBACK: Record<Category, SparePart[]> = {
  Engines: engines,
  Transmissions: transmissions,
  "Body Parts": bodyParts,
};

function toSparePart(p: DbProduct): SparePart {
  return {
    id: p.id,
    name: p.name,
    category: p.category as Category,
    make: p.make || "Universal",
    model: p.model || "",
    yearRange: p.year_range || "",
    fitment: p.fitment || "",
    price: p.price || "",
    warranty: p.warranty || "3 Month Used Parts Warranty",
    condition: p.condition || "Used",
    stock: p.stock || "In Stock",
    description: p.description || "",
    image_url: p.image_url || undefined,
  };
}

export function useProducts(category: Category): SparePart[] {
  const [products, setProducts] = useState<SparePart[]>(FALLBACK[category]);

  useEffect(() => {
    let active = true;
    supabase
      .from("products")
      .select("*")
      .is("deleted_at", null)
      .eq("category", category)
      .order("created_at", { ascending: true })
      .then(({ data, error }) => {
        if (!active) return;
        if (error || !data || data.length === 0) {
          setProducts(FALLBACK[category]);
        } else {
          setProducts((data as DbProduct[]).map(toSparePart));
        }
      });

    return () => {
      active = false;
    };
  }, [category]);

  return products;
}